// src/hooks/useNetlifyForm.ts
/* eslint-disable @typescript-eslint/no-unused-vars */
// src/hooks/useNetlifyForm.ts
// This hook helps handle Netlify form submissions through AJAX
import { useState, FormEvent } from 'react';

interface FormValues {
  [key: string]: string;
}

interface UseNetlifyFormReturn {
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
  formStatus: 'idle' | 'submitting' | 'success' | 'error';
  resetForm: () => void;
}

/**
 * Custom hook for handling Netlify form submissions
 * @param formName The name of the form (must match the hidden HTML form)
 * @param _initialValues Initial values for the form fields
 * @returns Form handling utilities
 */
export const useNetlifyForm = (
  formName: string,
  _initialValues: FormValues = {},
): UseNetlifyFormReturn => {
  const [formStatus, setFormStatus] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');

  const encode = (data: FormValues): string => {
    return Object.keys(data)
      .map(
        (key) => encodeURIComponent(key) + '=' + encodeURIComponent(data[key]),
      )
      .join('&');
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const formValues: FormValues = {};

    // Convert FormData to a regular object
    formData.forEach((value, key) => {
      formValues[key] = value.toString();
    });

    fetch('/', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: encode({
        'form-name': formName,
        ...formValues,
      }),
    })
      .then(() => {
        setFormStatus('success');
        form.reset();
      })
      .catch((error) => {
        console.error('Form submission error:', error);
        setFormStatus('error');
      });
  };

  const resetForm = () => {
    setFormStatus('idle');
  };

  return {
    handleSubmit,
    formStatus,
    resetForm,
  };
};
